import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ccremoteIcon from '../assets/ccremote-icon.png';
import { trackPageView } from '../utils/analytics';

const CCRemoteGuideCN = () => {
  useEffect(() => {
    trackPageView('CC Remote Guide CN');
  }, []);

  return (
    <div className="privacy-container guide-container">
      <header className="privacy-header">
        <div className="app-info">
          <div className="app-icon">
            <img src={ccremoteIcon} alt="CC Remote" />
          </div>
          <div className="app-details">
            <h1>CC Remote</h1>
            <p>Claude Code 远程控制工具</p>
          </div>
        </div>
        <div className="header-actions">
          <Link to="/ccremote" className="lang-switch">EN</Link>
          <Link to="/" className="back-link">← 返回首页</Link>
        </div>
      </header>

      <main className="privacy-content">
        <div className="privacy-section">
          <h2>CC Remote 是什么？</h2>
          <p>CC Remote 让你在 iPhone 或 iPad 上远程监控和操控电脑上运行的 Claude Code 会话。通过局域网连接，无需云服务。</p>
          <ul className="guide-list">
            <li>实时会话监控面板</li>
            <li>远程终端，支持完整输入</li>
            <li>自动发现局域网内的服务器</li>
            <li>等待状态提醒 — 不错过任何工具调用审批</li>
            <li>会话归档，保持面板整洁</li>
          </ul>
        </div>

        <div className="privacy-section">
          <h2>安装 CC Remote Server</h2>
          <p>CC Remote 需要在电脑上运行一个服务器。选择以下任一方式安装：</p>

          <h3 className="guide-subtitle">方式一：macOS 菜单栏应用（推荐）</h3>
          <ol className="guide-list">
            <li>从 GitHub 下载 <a href="https://github.com/AndyChang33/cc-remote/releases/latest/download/CCRemoteServer-v1.1.0.dmg" className="guide-link">CCRemoteServer.dmg</a></li>
            <li>打开 DMG，将应用拖入 Applications 文件夹</li>
            <li>启动应用 — 菜单栏会出现一个终端图标</li>
            <li>点击图标 → <strong>Start Server</strong></li>
          </ol>

          <h3 className="guide-subtitle">方式二：命令行</h3>
          <div className="guide-code">
            <code>
              git clone https://github.com/AndyChang33/cc-remote.git<br />
              cd cc-remote<br />
              npm install<br />
              npm start
            </code>
          </div>
          <p>服务器默认运行在 <strong>3456</strong> 端口，可通过环境变量自定义：</p>
          <div className="guide-code">
            <code>PORT=8080 CC_WORK_DIR=/path/to/project npm start</code>
          </div>

          <h3 className="guide-subtitle">方式三：Docker</h3>
          <div className="guide-code">
            <code>
              git clone https://github.com/AndyChang33/cc-remote.git<br />
              cd cc-remote<br />
              docker compose up -d<br />
              node scripts/ccrd.js        # 宿主机 daemon，用于创建终端
            </code>
          </div>
          <p>容器运行服务器（<code className="guide-inline-code">NO_PTY=1</code>），终端会话通过 ccrd daemon 在宿主机上创建。设置 shell alias 可简化操作：</p>
          <div className="guide-code">
            <code>
              # 添加到 ~/.zshrc<br />
              alias ccd="/path/to/cc-remote/scripts/cc-docker-start.sh"<br />
              alias ccd-stop="/path/to/cc-remote/scripts/cc-docker-stop.sh"
            </code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>从设备连接</h2>
          <ol className="guide-list">
            <li>确保 iPhone/iPad 和电脑在<strong>同一 WiFi 网络</strong>下</li>
            <li>打开设备上的 CC Remote App</li>
            <li>App 会自动发现服务器 — 也可手动输入地址（如 <code className="guide-inline-code">192.168.x.x:3456</code>）</li>
            <li>点击连接，开始管理你的会话</li>
          </ol>
        </div>

        <div className="privacy-section">
          <h2>三种使用模式</h2>

          <h3 className="guide-subtitle">1. 交互式终端</h3>
          <p>在面板中创建新会话，获得完整的远程终端 — 运行命令、启动 <code className="guide-inline-code">claude</code>，直接在手机上审批工具调用。</p>

          <h3 className="guide-subtitle">2. 监控模式（Hooks）</h3>
          <p>配置 Claude Code hooks 将事件上报到服务器，面板会显示只读的监控卡片，包含工具调用日志和 diff。将以下内容添加到 <code className="guide-inline-code">~/.claude/settings.json</code>：</p>
          <div className="guide-code">
            <code>
{`{
  "hooks": {
    "PreToolUse": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }],
    "PostToolUse": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }],
    "Notification": [{ "hooks": [{ "type": "command",
      "command": "curl -s -X POST http://localhost:3456/hook -H 'Content-Type: application/json' -d @- > /dev/null 2>&1 || true" }] }]
  }
}`}
            </code>
          </div>

          <h3 className="guide-subtitle">3. 代理模式（ccr）</h3>
          <p>在终端中使用 <code className="guide-inline-code">ccr</code> 代替 <code className="guide-inline-code">claude</code>。网页/App 会实时镜像终端输出，你可以从手机发送输入 — 包括 y/n 确认。</p>
          <div className="guide-code">
            <code>
              ccr                    # 等同于 claude<br />
              ccr --resume           # 恢复上次对话
            </code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>公网访问（可选）</h2>
          <p>默认情况下，服务器只能在局域网内访问。使用 ngrok 可以从任何地方访问：</p>
          <div className="guide-code">
            <code>
              npm start<br />
              ngrok http 3456
            </code>
          </div>
          <p>为了安全，建议添加认证：</p>
          <div className="guide-code">
            <code>ngrok http 3456 --basic-auth="user:yourpassword"</code>
          </div>
        </div>

        <div className="privacy-section">
          <h2>了解更多</h2>
          <p>CC Remote 服务端完全开源。访问 <a href="https://github.com/AndyChang33/cc-remote" className="guide-link">GitHub 仓库</a> 查看完整文档、提交 Issue 或参与贡献。</p>
        </div>
      </main>
    </div>
  );
};

export default CCRemoteGuideCN;
